import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { parseBRLInput } from "@/lib/price";
import { InlineEditShell } from "./InlineEditShell";
import { useInlineEdit } from "./useInlineEdit";

/**
 * Valor monetário editável por clique.
 *
 * Aceita o que a pessoa digita em pt-BR ("R$ 1.234,56") e envia no formato que
 * o servidor exige (`^\d+(\.\d{1,2})?$`). Entrada inválida vira erro na hora,
 * antes do request — deixar o zod recusar devolveria "Valor inválido (use
 * 1234.56)" para alguém que digitou exatamente o formato do próprio país.
 */
export default function InlineEditCurrency({
  value,
  onCommit,
  canEdit,
  label = "valor",
  children,
  className,
  inputClassName,
  align = "left",
  disabledReason,
}: {
  /** Valor numeric do Postgres, como string. */
  value: string | null;
  onCommit: (next: string) => Promise<void>;
  canEdit: boolean;
  label?: string;
  children: React.ReactNode;
  className?: string;
  inputClassName?: string;
  align?: "left" | "right";
  disabledReason?: string;
}) {
  const [localError, setLocalError] = useState<string | null>(null);

  const edit = useInlineEdit<string>({
    value: value ?? "0",
    disabled: !canEdit,
    // "1800" e "1800.00" são o mesmo dinheiro.
    isEqual: (a, b) => Number(a) === Number(b),
    onCommit: async (next) => {
      const parsed = parseBRLInput(next);
      if (parsed === null) {
        throw new Error("Valor inválido. Use algo como 1.234,56");
      }
      await onCommit(parsed);
    },
  });

  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (edit.isEditing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [edit.isEditing]);

  // `localError` é a validação de formato (antes de sair da tela);
  // `edit.error` é a recusa do servidor. Ambos mantêm o campo aberto com o
  // texto digitado, em vez de descartar o trabalho.
  const shownError = localError ?? (edit.hasError ? edit.error : null);

  if (edit.isEditing) {
    return (
      <div className="relative">
        <Input
          ref={ref}
          value={edit.draft ?? ""}
          inputMode="decimal"
          onChange={(e) => {
            edit.setDraft(e.target.value);
            setLocalError(
              e.target.value.trim() && parseBRLInput(e.target.value) === null
                ? "Valor inválido"
                : null,
            );
          }}
          onKeyDown={edit.fieldProps.onKeyDown}
          onBlur={edit.fieldProps.onBlur}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "h-auto py-1 tabular-nums",
            align === "right" && "text-right",
            shownError && "border-destructive",
            inputClassName,
          )}
          aria-label={label}
          aria-invalid={!!shownError}
        />
        {shownError && (
          <p role="alert" className="mt-0.5 text-[10px] text-destructive">
            {shownError}
            {edit.hasError && " · Esc para descartar"}
          </p>
        )}
      </div>
    );
  }

  return (
    <InlineEditShell
      canEdit={canEdit}
      status={edit.status}
      error={edit.error}
      label={label}
      triggerProps={edit.triggerProps}
      className={className}
      align={align}
      disabledReason={disabledReason}
    >
      {children}
    </InlineEditShell>
  );
}
